import React, { useMemo, useCallback, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Stack,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Divider,
  CardActions,
  CardActionArea,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ExplorePostModal from '../Explore/ExplorePostModal';
import ConfirmDialog from '../ui/Modal/ConfirmDialog';

export default function ExploreSection({
  entries,
  handleEdit,
  handleDelete,
  setOpen,
  open,
  form,
  setForm,
  handleSubmitEdit,
  propertyId,
}) {
  const navigate = useNavigate();
  const [confirmState, setConfirmState] = useState({ open: false, postId: null, title: '' });

  const sortedEntries = useMemo(() => {
    return [...(entries || [])].sort((a, b) => {
      const da = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
      const db = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
      return db - da;
    });
  }, [entries]);

  const handleSave = useCallback((post) => {
    const payload = { ...post, propertyId };
    handleSubmitEdit(payload, 'explore');
  }, [handleSubmitEdit, propertyId]);

  const handlePreview = useCallback((postId) => {
    navigate(`/hotelInfo/explore/${postId}`, {
      state: { returnTo: '/hotelInfo?tab=6' },
    });
  }, [navigate]);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Explore Posts</Typography>
        <Button
          variant="contained"
          onClick={() => {
            setForm({});
            setOpen(true);
          }}
        >
          + Add Post
        </Button>
      </Box>

      <Stack spacing={2} sx={{ maxWidth: 720, mx: 'auto' }}>
        {sortedEntries.map((post) => (
          <Card
            key={post._id}
            variant="outlined"
            sx={{
              borderRadius: 4,
              overflow: 'hidden',
              borderColor: 'rgba(17,24,39,0.06)',
              boxShadow: '0 18px 32px rgba(17,24,39,0.12)',
              transition: 'box-shadow 0.25s ease, transform 0.25s ease',
              '&:hover': {
                boxShadow: '0 22px 40px rgba(17,24,39,0.16)',
                transform: 'translateY(-2px)',
              },
            }}
          >
            <CardActionArea onClick={() => handlePreview(post._id)}>
              {post.imageUrl ? (
                <CardMedia
                  component="img"
                  image={post.imageUrl}
                  alt={post.title}
                  sx={{
                    height: 168,
                    objectFit: 'cover',
                  }}
                />
              ) : null}
              <CardContent sx={{ py: 2.5, px: 3 }}>
                <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1, mb: 0.5 }}>
                  {post.heroEyebrow || 'Featured Story'}
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    lineHeight: 1.4,
                    mb: 1,
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  {post.title}
                </Typography>
                {post.summary ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: 2,
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {post.summary}
                  </Typography>
                ) : null}
                <Stack direction="row" spacing={1.5} alignItems="center">
                  {post.readMinutes ? (
                    <Chip size="small" label={`${post.readMinutes} min read`} variant="outlined" />
                  ) : null}
                  {post.status ? (
                    <Chip
                      size="small"
                      label={post.status}
                      color={post.status === 'published' ? 'success' : post.status === 'draft' ? 'warning' : 'default'}
                      variant="outlined"
                    />
                  ) : null}
                  {post.tags?.length ? (
                    <Typography variant="caption" color="text.secondary">
                      {post.tags.slice(0, 2).join(', ')}
                      {post.tags.length > 2 ? '…' : ''}
                    </Typography>
                  ) : null}
                </Stack>
              </CardContent>
            </CardActionArea>
            <Divider />
            <CardActions sx={{ justifyContent: 'flex-end', px: 2.5, py: 2 }}>
              <Button size="small" variant="outlined" onClick={() => handlePreview(post._id)} sx={{ borderRadius: 999 }}>
                Preview
              </Button>
              <Button size="small" onClick={() => handleEdit(post)} sx={{ borderRadius: 999 }}>
                Edit
              </Button>
              <Button
                size="small"
                color="error"
                onClick={() => setConfirmState({ open: true, postId: post._id, title: post.title || 'this story' })}
                sx={{ borderRadius: 999 }}
              >
                Delete
              </Button>
            </CardActions>
          </Card>
        ))}
      </Stack>

      <ExplorePostModal
        open={open}
        onClose={() => setOpen(false)}
        initialData={form}
        onSubmit={handleSave}
        editingId={form?._id}
      />
      <ConfirmDialog
        open={confirmState.open}
        title="Delete Explore Post"
        description={`Are you sure you want to delete “${confirmState.title}”? This cannot be undone.`}
        confirmText="Delete"
        onConfirm={() => {
          if (confirmState.postId) {
            handleDelete(confirmState.postId, 'explore');
          }
        }}
        onCancel={() => setConfirmState({ open: false, postId: null, title: '' })}
      />
    </Box>
  );
}
